import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Power, PowerOff, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const TYPE_COLORS = {
    naming_convention: "default",
    layout_preference: "secondary",
    color_palette: "outline",
    component_library: "default",
    data_model_convention: "secondary",
};

export default function CompanyPatternRow({ pattern, onAction }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const p = pattern;
    const hasTrace = p.reasoning || p.sourceData;

    return (
        <div className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={p.isActive ? "default" : "outline"}>
                    {p.isActive
                        ? t("admin.intelligence.companyPatterns.active")
                        : t("admin.intelligence.companyPatterns.inactive")}
                </Badge>
                <Badge variant={TYPE_COLORS[p.patternType] || "outline"}>
                    {t(`admin.intelligence.companyPatterns.types.${p.patternType}`, p.patternType)}
                </Badge>
                <span className="text-xs text-muted-foreground font-medium">{p.companyName}</span>
                <span className="text-xs text-muted-foreground">
                    {t("admin.intelligence.rules.confidence")}: {Math.round(p.confidence * 100)}%
                </span>
                <span className="text-xs text-muted-foreground">
                    {p.exampleCount} {t("admin.intelligence.rules.examples")}
                </span>
            </div>
            <p className="text-sm whitespace-pre-wrap">{p.pattern}</p>
            <div className="flex gap-2 pt-1 flex-wrap">
                <Button
                    size="sm"
                    variant={p.isActive ? "outline" : "default"}
                    onClick={() => onAction(p.id, p.isActive ? "deactivate" : "activate")}
                >
                    {p.isActive
                        ? <><PowerOff className="h-3 w-3 mr-1" />{t("admin.intelligence.companyPatterns.deactivate")}</>
                        : <><Power className="h-3 w-3 mr-1" />{t("admin.intelligence.companyPatterns.activate")}</>}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => onAction(p.id, "delete")}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    {t("common.delete")}
                </Button>
                {hasTrace && (
                    <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}>
                        {expanded
                            ? <ChevronUp className="h-3 w-3 mr-1" />
                            : <ChevronDown className="h-3 w-3 mr-1" />}
                        {t("admin.intelligence.companyPatterns.showTrace")}
                    </Button>
                )}
            </div>
            {expanded && <PatternTrace pattern={p} />}
        </div>
    );
}

function PatternTrace({ pattern }) {
    const { t } = useTranslation();
    const source = pattern.sourceData || {};

    return (
        <div className="mt-2 border-t pt-3 space-y-3 text-sm">
            {pattern.reasoning && (
                <div>
                    <h4 className="font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.companyPatterns.reasoning")}
                    </h4>
                    <p className="bg-muted/50 rounded p-2">{pattern.reasoning}</p>
                </div>
            )}
            {(source.totalInteractions != null) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <TraceMetric label={t("admin.intelligence.companyPatterns.traceInteractions")} value={source.totalInteractions} />
                    <TraceMetric label={t("admin.intelligence.companyPatterns.traceAccepted")} value={source.acceptedCount} />
                    <TraceMetric label={t("admin.intelligence.companyPatterns.traceRejected")} value={source.rejectedCount} />
                    {source.appCategories?.length > 0 && (
                        <TraceMetric label={t("admin.intelligence.companyPatterns.traceCategories")} value={source.appCategories.join(", ")} />
                    )}
                </div>
            )}
            {source.sampleAccepted?.length > 0 && (
                <div>
                    <h4 className="font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.companyPatterns.traceAcceptedSamples")}
                    </h4>
                    <ul className="space-y-1">
                        {source.sampleAccepted.map((s, i) => (
                            <li key={i} className="bg-muted/50 rounded p-2 text-xs">
                                <span className="font-medium">{s.request}</span>
                                {s.signal && <span className="text-muted-foreground ml-2">— {s.signal}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {source.sampleRejected?.length > 0 && (
                <div>
                    <h4 className="font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.companyPatterns.traceRejectedSamples")}
                    </h4>
                    <ul className="space-y-1">
                        {source.sampleRejected.map((s, i) => (
                            <li key={i} className="bg-muted/50 rounded p-2 text-xs">
                                <span className="font-medium">{s.request}</span>
                                {s.signal && <span className="text-muted-foreground ml-2">— {s.signal}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function TraceMetric({ label, value }) {
    return (
        <div className="bg-muted/50 rounded p-2">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="font-medium">{value}</div>
        </div>
    );
}
